import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './DistrictsList.css';

const MasterFormsList = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await axios.get('/api/forms');
      setForms(response.data);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/forms/${id}`);
      setForms(forms.filter(f => f.id !== id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete form:', error);
      // A 409 means the template is still in use: the server explains what depends on it and
      // what to do instead. Showing a generic failure here would read as a bug and invite a
      // retry, when the delete is being refused on purpose to protect submitted forms.
      const data = error?.response?.data;
      if (error?.response?.status === 409 && data?.error) {
        setDeleteError([data.error, data.detail, data.remedy].filter(Boolean).join('\n\n'));
        setDeleteConfirm(null);
        return;
      }
      alert('Failed to delete form');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="districts-list">
      <div className="page-header">
        <h2>Master Forms</h2>
        <Link to="/superadmin/forms/add" className="btn-primary">Add Master Form</Link>
      </div>

      {deleteError && (
        <div className="modal-overlay" onClick={() => setDeleteError('')}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Cannot delete this form</h3>
            <p style={{ whiteSpace: 'pre-line' }}>{deleteError}</p>
            <div className="modal-actions">
              <button onClick={() => setDeleteError('')} className="btn-primary">OK</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Form</h3>
            <p>
              Are you sure you want to delete this form? This action cannot be undone.
            </p>
            <p>
              A master form is a template. If any district was set up from it, the delete will be
              refused to protect their forms and submissions - deactivate it instead.
            </p>
            <div className="modal-actions">
              <button onClick={() => handleDelete(deleteConfirm)} className="btn-danger">Delete</button>
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>Form Name</th>
            <th>Description</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {forms.length === 0 ? (
            <tr>
              <td colSpan="5" style={{ textAlign: 'center' }}>No forms found</td>
            </tr>
          ) : (
            forms.map(form => (
              <tr key={form.id}>
                <td>{form.form_name}</td>
                <td>{form.form_description || '-'}</td>
                <td>{form.is_active ? 'Active' : 'Inactive'}</td>
                <td>{new Date(form.created_at).toLocaleDateString()}</td>
                <td className="actions">
                  <button onClick={() => navigate(`/superadmin/forms/view/${form.id}`)} className="btn-edit">View</button>
                  <button onClick={() => navigate(`/superadmin/forms/edit/${form.id}`)} className="btn-edit">Edit</button>
                  <button onClick={() => navigate(`/superadmin/form-instances/create/${form.id}`)} className="btn-primary">Create Instance</button>
                  <button onClick={() => setDeleteConfirm(form.id)} className="btn-delete">Delete</button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default MasterFormsList;

